import React, { useMemo } from "react";
import moment from "moment";
import { Box, Chip, Typography } from "@mui/material";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import DescriptionIcon from "@mui/icons-material/Description";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import "./HistoricoTimeline.scss";
import BadgeUnidade from "../../../../../shared/components/badgeUnidade/BadgeUnidade";

const TYPE_META = {
  sessao: {
    label: "Sessão",
    icon: <MedicalServicesIcon fontSize="small" />,
    color: "#22c55e",
  },
  tratamentoIsolado: {
    label: "Tratamento isolado",
    icon: <LocalHospitalIcon fontSize="small" />,
    color: "#06b6d4",
  },
  evolucao: {
    label: "Evolução",
    icon: <DescriptionIcon fontSize="small" />,
    color: "#f59e0b",
  },
  naoExecutado: {
    label: "Atendido, não executado",
    icon: <EventBusyIcon fontSize="small" />,
    color: "#ef4444",
  },
};

/* Motivos guardados como constante no servidor. Sem isto o histórico mostrava
   CRIANCA_NAO_COLABOROU tal e qual. */
const MOTIVOS_NAO_EXECUCAO = {
  CRIANCA_NAO_COLABOROU: "A criança não colaborou",
  PACIENTE_NAO_COLABOROU: "O paciente não colaborou",
  CONDICAO_CLINICA_INADEQUADA: "Condição clínica inadequada",
  EQUIPAMENTO_INDISPONIVEL: "Equipamento indisponível",
  OUTRO: "Outro motivo",
};


function buildSessaoMedico(sessao, proc) {
  // tenta várias possibilidades (depende de como vem do backend)
  // sessao.medico pode ser objeto populado ou apenas id
  return (
    sessao?.medico?.name ||
    sessao?.medico?.nome ||
    proc?.medico?.name ||
    proc?.medico_responsavel?.name ||
    ""
  );
}

function HistoricoTimeline({
  planosTratamento,
  tratamentoIsolados,
  evolucoes,
  naoExecutados,
}) {
  const events = useMemo(() => {
    const out = [];

    // Sessões dos planos
    (planosTratamento || []).forEach((plano) => {
      (plano.procedimentos || []).forEach((proc) => {
        (proc.sessoes || []).forEach((sessao, idx) => {
          const date =
            sessao?.data_inicio_execucao || proc?.data_inicio_execucao;
          if (!date) return;

          out.push({
            type: "sessao",
            date,
            dateEnd: sessao?.data_fim_execucao,
            title: proc?.tratamento?.designacao || "Procedimento",
            medico: buildSessaoMedico(sessao, proc),
            planoNumero: plano?.numero,
            sessaoNumero: idx + 1,
            totalSessoes: (proc.sessoes || []).length,
            dente: proc?.dente,
            faces: proc?.faces || [],
            /* O que o dentista escreveu nesta sessão. Era o único dos quatro
               tipos de evento que não trazia texto nenhum: os tratamentos
               isolados traziam o `relatorio` e as evoluções o `evolucao`, mas
               a sessão de um plano ficava só com a designação do tratamento.
               Quem escrevia a evolução na sessão não a voltava a encontrar na
               timeline. */
            description: sessao?.evolucao || "",
            // Onde a sessão foi executada — pode não ser a unidade do plano.
            estabelecimento: sessao?.estabelecimento,
            status: proc?.procedimento_completo ? "Finalizado" : "Em andamento",
          });
        });
      });
    });

    // Tratamentos isolados
    (tratamentoIsolados || []).forEach((t) => {
      if (!t?.data_inicio_execucao) return;
      out.push({
        type: "tratamentoIsolado",
        date: t.data_inicio_execucao,
        dateEnd: t.data_fim_execucao,
        title:
          t?.tratamento?.designacao ||
          t?.categoriaTratamento?.categoria ||
          "Tratamento",
        medico: t?.medico?.name || t?.medico?.nome || "",
        dente: t?.dente,
        faces: t?.faces || [],
        description: t?.relatorio || "",
        // Num isolado a unidade está no próprio registo: não há sessões.
        estabelecimento: t?.estabelecimento,
        /* Copiado de uma ficha em papel, não executado no sistema. Quem lê o
           histórico tem de o saber: a data é a do papel, e o sistema não
           acompanhou nada disto. */
        importado: t?.historicoImportado === true,
        status: "Executado",
      });
    });

    // Evoluções
    (evolucoes || []).forEach((e) => {
      if (!e?.data_inicio_execucao) return;
      out.push({
        type: "evolucao",
        date: e.data_inicio_execucao,
        dateEnd: e.data_fim_execucao,
        title: "Evolução",
        medico: e?.medico?.name || e?.medico?.nome || "",
        description: e?.evolucao || "",
      });
    });

    // Atendimentos sem execução — o paciente veio, nada foi feito.
    (naoExecutados || []).forEach((a) => {
      if (!a?.data_inicio_execucao) return;
      const motivo =
        MOTIVOS_NAO_EXECUCAO[a.motivoNaoExecucaoTipo] ||
        a.motivoNaoExecucaoTipo ||
        "";
      out.push({
        type: "naoExecutado",
        date: a.data_inicio_execucao,
        dateEnd: a.data_fim_execucao,
        title: a.designacao,
        medico: a?.medico?.name || a?.medico?.nome || "",
        dente: a?.dente,
        faces: a?.faces || [],
        motivo,
        description: a.motivoNaoExecucaoDetalhes || "",
      });
    });

    return out.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [planosTratamento, tratamentoIsolados, evolucoes, naoExecutados]);

  const grouped = useMemo(() => {
    const map = new Map();
    events.forEach((ev) => {
      const key = moment(ev.date).format("YYYY-MM-DD");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    });
    return Array.from(map.entries()); // [[day, events[]], ...] já na ordem (mais recente primeiro)
  }, [events]);

  if (!events.length) {
    return (
      <Box className="htl-empty">
        <Typography variant="body1" color="textSecondary">
          Nenhum evento registado ainda.
        </Typography>
      </Box>
    );
  }

  return (
    <div className="htl">
      {grouped.map(([day, dayEvents]) => (
        <div key={day} className="htl-day">
          <div className="htl-day-header">
            <Typography className="htl-day-title">
              {moment(day).format("DD [de] MMMM [de] YYYY")}
            </Typography>
            <Chip
              size="small"
              label={`${dayEvents.length} ${
                dayEvents.length === 1 ? "evento" : "eventos"
              }`}
              className="htl-day-chip"
            />
          </div>

          <div className="htl-list">
            {dayEvents.map((ev, idx) => {
              const meta = TYPE_META[ev.type] || {};
              const time =
                moment(ev.date).format("HH:mm") +
                (ev.dateEnd ? `–${moment(ev.dateEnd).format("HH:mm")}` : "");

              return (
                <div key={`${ev.type}-${day}-${idx}`} className="htl-item">
                  <div
                    className="htl-dot"
                    style={{ borderColor: meta.color }}
                  />
                  <div className="htl-line" />

                  <div className="htl-content">
                    <div className="htl-top">
                      <span className="htl-time">{time}</span>

                      <Chip
                        size="small"
                        icon={meta.icon}
                        label={meta.label}
                        sx={{
                          backgroundColor: `${meta.color}12`,
                          color: meta.color,
                          fontWeight: 700,
                        }}
                      />

                      {ev.status && (
                        <span
                          className={`htl-status ${
                            ev.status === "Finalizado" ||
                            ev.status === "Executado"
                              ? "ok"
                              : "warn"
                          }`}
                        >
                          {ev.status}
                        </span>
                      )}
                    </div>

                    <div className="htl-title">
                      {ev.type === "evolucao" ? (
                        <Typography variant="body2">
                          {ev.description}
                        </Typography>
                      ) : (
                        <Typography variant="subtitle1">{ev.title}</Typography>
                      )}
                    </div>

                    <div className="htl-meta">
                      {ev.medico && (
                        <span className="htl-pill">
                          <strong>Médico:</strong> {ev.medico}
                        </span>
                      )}

                      {/* A MESMA pastilha das listas, e não uma etiqueta
                          própria da timeline: a unidade é a mesma informação
                          em todo o lado e deve ler-se sempre igual. Uma marca
                          que muda de forma conforme o ecrã obriga a reaprendê-la
                          a cada sítio. */}
                      <BadgeUnidade
                        estabelecimento={ev.estabelecimento}
                        prefixo="Executado em"
                      />

                      {ev.importado && (
                        <span
                          className="htl-pill htl-pill--importado"
                          title="Registo copiado de uma ficha em papel — não foi executado no sistema"
                        >
                          Importado
                        </span>
                      )}

                      {ev.planoNumero && (
                        <span className="htl-pill">
                          <strong>Plano:</strong> #{ev.planoNumero}
                        </span>
                      )}

                      {ev.sessaoNumero && ev.totalSessoes && (
                        <span className="htl-pill">
                          <strong>Sessão:</strong> {ev.sessaoNumero}/
                          {ev.totalSessoes}
                        </span>
                      )}

                      {ev.dente && (
                        <span className="htl-pill">
                          <strong>Dente:</strong> {ev.dente}
                        </span>
                      )}

                      {ev.faces && ev.faces.length > 0 && (
                        <span className="htl-pill">
                          <strong>Faces:</strong> {ev.faces.join(", ")}
                        </span>
                      )}

                      {ev.motivo && (
                        <span className="htl-pill">
                          <strong>Motivo:</strong> {ev.motivo}
                        </span>
                      )}

                    </div>

                    {/* Qualquer evento com texto mostra-o aqui. A condição
                        enumerava os tipos um a um e a sessão de plano tinha
                        ficado de fora — enumerar tipos obriga a lembrar deste
                        sítio sempre que se acrescenta um, e foi o que falhou.
                        A evolução é a exceção: já sai acima, como título. */}
                    {ev.type !== "evolucao" && ev.description && (
                      <div className="htl-note">
                        <Typography variant="body2" color="textSecondary">
                          {ev.description}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default HistoricoTimeline;
